import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { notificationService } from '@/services/notificationService';
import { cn } from '@/lib/utils';

/**
 * Header notification bell (F043).
 * Fetches unseen-count on mount + on window focus.
 * Badge: warning-styled when escalation > 0, muted when only system > 0, no badge at zero.
 * Deep-links to /notifications.
 * On fetch error: renders bell with no badge (graceful degradation).
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const [escalation, setEscalation] = useState(0);
  const [system, setSystem] = useState(0);

  const fetchCounts = () => {
    notificationService
      .unseenCount()
      .then(({ escalation: e, system: s }) => {
        setEscalation(e);
        setSystem(s);
      })
      .catch(() => {
        // graceful: show no badge on error
      });
  };

  useEffect(() => {
    fetchCounts();

    window.addEventListener('focus', fetchCounts);
    return () => {
      window.removeEventListener('focus', fetchCounts);
    };
  }, []);

  const total = escalation + system;
  const hasWarning = escalation > 0;
  const hasMuted = total > 0 && escalation === 0;

  const ariaLabel = t('notifications.bell.aria', {
    escalation,
    system,
    total,
  });

  return (
    <Link
      to="/notifications"
      aria-label={ariaLabel}
      data-testid="notification-bell"
      className={cn(
        'relative inline-flex items-center justify-center',
        'h-11 w-11 rounded-md transition-colors hover:bg-muted',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      <Bell className="h-5 w-5" aria-hidden="true" />

      {total > 0 ? (
        <span
          data-testid="notification-badge"
          className={cn(
            'absolute -top-0.5 -right-0.5 flex h-5 min-w-[1.25rem] items-center justify-center',
            'rounded-full px-1 text-xs font-semibold leading-none',
            hasWarning && 'bg-warning text-primary-foreground',
            hasMuted && 'bg-muted-foreground/30 text-foreground'
          )}
          aria-hidden="true"
        >
          {total > 99 ? '99+' : total}
        </span>
      ) : null}
    </Link>
  );
}
